import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, Switch, Typography, useApp, Popconfirm, QRCode, Image, Row, Col, Alert,
} from '../../ui';
import { Plus, Pencil, Trash2, Star, Landmark, Store } from 'lucide-react';
import { request } from '../../api/client';
import { FileUploadField } from '../../components/FileUploadField';

/**
 * Seller (your own company) statutory identity printed on every invoice & proforma.
 * Singleton — loaded from / saved to /company/profile (COMPANY.PROFILE.EDIT).
 */
function CompanyProfileCard() {
  const qc = useQueryClient();
  const { message } = useApp();
  const [form] = Form.useForm();
  const { data, isLoading } = useQuery({ queryKey: ['company-profile'], queryFn: () => request<any>({ url: '/company/profile' }) });
  useEffect(() => { if (data) form.setFieldsValue(data); }, [data, form]);

  const saveMut = useMutation({
    mutationFn: (vals: any) => request({ url: '/company/profile', method: 'PUT', data: vals }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['company-profile'] }); message.success('Company profile saved'); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save profile'),
  });

  return (
    <Card style={{ marginBottom: 16 }} loading={isLoading}>
      <div style={{ marginBottom: 8 }}>
        <Typography.Title level={4} style={{ marginBottom: 2 }}><Store size={16} /> Company Profile</Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          Your seller identity printed on every <b>tax invoice</b> &amp; <b>proforma</b> (header, GST details, terms, footer).
        </Typography.Paragraph>
      </div>
      <Form form={form} layout="vertical" requiredMark="optional" onFinish={(v) => saveMut.mutate(v)}>
        <Row gutter={16}>
          <Col xs={24} md={12}><Form.Item name="legalName" label="Legal name" rules={[{ required: true }]}><Input placeholder="GREENBAY BIOTECH INTERNATIONAL PRIVATE LIMITED" /></Form.Item></Col>
          <Col xs={24} md={12}><Form.Item name="tradeName" label="Trade name"><Input placeholder="Optional" /></Form.Item></Col>
          <Col span={24}><Form.Item name="address" label="Registered address" rules={[{ required: true }]}><Input.TextArea rows={2} placeholder="Warehouse / office address" /></Form.Item></Col>
          <Col xs={12} md={8}><Form.Item name="city" label="City"><Input /></Form.Item></Col>
          <Col xs={12} md={8}><Form.Item name="stateName" label="State"><Input placeholder="MAHARASHTRA" /></Form.Item></Col>
          <Col xs={8} md={3}><Form.Item name="stateCode" label="State code"><Input placeholder="27" /></Form.Item></Col>
          <Col xs={16} md={5}><Form.Item name="pincode" label="Pincode"><Input /></Form.Item></Col>
          <Col xs={24} md={8}><Form.Item name="gstin" label="GSTIN"><Input placeholder="27AAFCG8670G1ZB" /></Form.Item></Col>
          <Col xs={12} md={8}><Form.Item name="cin" label="CIN"><Input placeholder="U74900WB2015PTC205115" /></Form.Item></Col>
          <Col xs={12} md={8}><Form.Item name="pan" label="PAN"><Input /></Form.Item></Col>
          <Col xs={24} md={8}><Form.Item name="tel" label="Telephone"><Input placeholder="+91 (33) 4064 6022" /></Form.Item></Col>
          <Col xs={12} md={8}><Form.Item name="email" label="Email"><Input placeholder="support@greenbaybio.com" /></Form.Item></Col>
          <Col xs={12} md={8}><Form.Item name="website" label="Website"><Input placeholder="www.greenbaybio.com" /></Form.Item></Col>
          <Col xs={12} md={8}><Form.Item name="mobile" label="Mobile (on documents)"><Input /></Form.Item></Col>
          <Col xs={24} md={16}><Form.Item name="msmeUdyam" label="MSME (UDYAM) numbers"><Input placeholder="WB-10-0000909 | OD-30-0022442" /></Form.Item></Col>
          <Col span={24}><Form.Item name="terms" label="Terms &amp; conditions"><Input.TextArea rows={2} placeholder="e.g. Interest @24% p.a. will be charged on payments made after 60 days from the bill date." /></Form.Item></Col>
          <Col span={24}><Form.Item name="declaration" label="Declaration"><Input placeholder="Certified that the particulars given above are true & correct" /></Form.Item></Col>
        </Row>
        <Button variant="primary" type="submit" loading={saveMut.isPending}>Save profile</Button>
      </Form>
    </Card>
  );
}

interface BankAccount {
  id: string;
  label?: string | null;
  accountHolderName: string;
  bankName: string;
  accountNumber: string;
  ifsc: string;
  branch?: string | null;
  upiId?: string | null;
  upiQrUrl?: string | null;
  notes?: string | null;
  isPrimary: boolean;
  isActive: boolean;
}

const upiString = (a: { upiId?: string | null; accountHolderName?: string }) =>
  a.upiId ? `upi://pay?pa=${encodeURIComponent(a.upiId)}&pn=${encodeURIComponent(a.accountHolderName || 'Green Bay Biotech')}&cu=INR` : '';

export function BankDetailsPage() {
  const qc = useQueryClient();
  const { message } = useApp();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [form] = Form.useForm<BankAccount>();
  const upiQrUrl = Form.useWatch('upiQrUrl', form);
  const upiId = Form.useWatch('upiId', form);
  const holder = Form.useWatch('accountHolderName', form);

  const { data: accounts, isLoading } = useQuery({
    queryKey: ['company-bank-accounts'],
    queryFn: () => request<BankAccount[]>({ url: '/company/bank-accounts' }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['company-bank-accounts'] });
  const fail = (e: any) => message.error(e.response?.data?.error?.message ?? 'Something went wrong');

  const saveMut = useMutation({
    mutationFn: (vals: any) =>
      editing
        ? request({ url: `/company/bank-accounts/${editing.id}`, method: 'PUT', data: vals })
        : request({ url: '/company/bank-accounts', method: 'POST', data: vals }),
    onSuccess: () => { invalidate(); setOpen(false); message.success(editing ? 'Account updated' : 'Account added'); },
    onError: fail,
  });
  const primaryMut = useMutation({
    mutationFn: (id: string) => request({ url: `/company/bank-accounts/${id}/primary`, method: 'POST' }),
    onSuccess: () => { invalidate(); message.success('Primary account updated'); },
    onError: fail,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => request({ url: `/company/bank-accounts/${id}`, method: 'DELETE' }),
    onSuccess: () => { invalidate(); message.success('Account deleted'); },
    onError: fail,
  });

  // Seeding is via the Form's initialValues + key (below), not setFieldsValue here: the Form remounts
  // on modal open and re-inits from initialValues, which would otherwise wipe a handler seed (blank) or
  // let a prior row's values leak in (stale). The backend auto-promotes the first account to primary.
  const openAdd = () => {
    setEditing(null);
    setOpen(true);
  };
  const openEdit = (a: BankAccount) => {
    setEditing(a);
    setOpen(true);
  };
  const submit = () => form.validateFields().then((v) => saveMut.mutate(v)).catch(() => undefined);

  const columns = [
    {
      title: 'Account',
      render: (_: any, a: BankAccount) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {a.bankName} {a.isPrimary && <Tag tone="green" icon={<Star size={16} />} style={{ marginLeft: 6 }}>Primary</Tag>}
            {!a.isActive && <Tag style={{ marginLeft: 6 }}>Inactive</Tag>}
          </div>
          <div style={{ fontSize: 12, color: '#888' }}>{a.accountHolderName}{a.label ? ` · ${a.label}` : ''}</div>
        </div>
      ),
    },
    { title: 'A/c No.', dataIndex: 'accountNumber' },
    { title: 'IFSC', dataIndex: 'ifsc' },
    { title: 'UPI', dataIndex: 'upiId', render: (v: string) => v || '—' },
    {
      title: 'Actions',
      align: 'right' as const,
      render: (_: any, a: BankAccount) => (
        <Space>
          {!a.isPrimary && (
            <Button variant="text" size="sm" icon={<Star size={16} />} loading={primaryMut.isPending} onClick={() => primaryMut.mutate(a.id)}>
              Set primary
            </Button>
          )}
          <Button variant="text" size="sm" icon={<Pencil size={16} />} aria-label="Edit" onClick={() => openEdit(a)} />
          <Popconfirm title="Delete this account?" onConfirm={() => deleteMut.mutate(a.id)} okButtonProps={{ danger: true }}>
            <Button variant="text" size="sm" icon={<Trash2 size={16} className="text-danger-600 dark:text-danger-500" />} aria-label="Delete" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const previewUpi = upiString({ upiId, accountHolderName: holder });

  return (
    <div>
      <CompanyProfileCard />

      <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <Typography.Title level={4} style={{ marginBottom: 2 }}><Landmark size={16} /> Bank Details</Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
            Company bank accounts shown on invoices &amp; proforma so retailers can pay. The <b>primary</b> account appears on documents.
          </Typography.Paragraph>
        </div>
        <Button variant="primary" icon={<Plus size={16} />} onClick={openAdd}>Add account</Button>
      </Space>

      <Card>
        <Table<BankAccount>
          rowKey="id"
          loading={isLoading}
          dataSource={accounts ?? []}
          columns={columns}
          pagination={false}
          locale={{ emptyText: 'No bank accounts yet — add one so it appears on invoices.' }}
        />
      </Card>

      <Modal
        title={editing ? 'Edit bank account' : 'Add bank account'}
        open={open}
        onOpenChange={(o) => { if (!o) setOpen(false); }}
        width={720}
        footer={<>
          <Button variant="default" onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="primary" loading={saveMut.isPending} onClick={submit}>{editing ? 'Save' : 'Add'}</Button>
        </>}
      >
        <Form
          key={editing ? `edit-${editing.id}` : 'add'}
          form={form}
          layout="vertical"
          requiredMark="optional"
          initialValues={editing ?? { isActive: true, isPrimary: false }}
        >
          <Row gutter={16}>
            <Col span={16}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="accountHolderName" label="Account holder name" rules={[{ required: true }]}>
                    <Input placeholder="e.g. Green Bay Biotech Pvt Ltd" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="bankName" label="Bank name" rules={[{ required: true }]}>
                    <Input placeholder="e.g. HDFC Bank" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="accountNumber" label="Account number" rules={[{ required: true }]}>
                    <Input placeholder="Account number" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="ifsc"
                    label="IFSC"
                    rules={[{ required: true }, { pattern: /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/, message: 'Invalid IFSC (e.g. HDFC0001234)' }]}
                    normalize={(v) => (v ? String(v).toUpperCase() : v)}
                  >
                    <Input placeholder="e.g. HDFC0001234" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="branch" label="Branch">
                    <Input placeholder="Branch (optional)" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="label" label="Label">
                    <Input placeholder="e.g. HDFC Current A/c (optional)" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="upiId" label="UPI ID" extra="The QR is generated from this automatically (unless you upload an image below).">
                    <Input placeholder="e.g. greenbay@hdfcbank" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="notes" label="Notes">
                    <Input.TextArea rows={2} placeholder="Anything else to show with the bank details (optional)" />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            <Col span={8}>
              <Form.Item label="UPI QR">
                <div style={{ textAlign: 'center', padding: 8, border: '1px dashed #d9d9d9', borderRadius: 8, marginBottom: 8 }}>
                  {upiQrUrl ? (
                    <Image src={upiQrUrl} width={120} height={120} style={{ objectFit: 'contain' }} />
                  ) : previewUpi ? (
                    <QRCode value={previewUpi} size={120} bordered={false} />
                  ) : (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>Enter a UPI ID or upload a QR image</Typography.Text>
                  )}
                </div>
                <Form.Item name="upiQrUrl" noStyle>
                  <FileUploadField category="payment-qr" buttonLabel="Upload QR image" preview={false} allowUrlPaste={false} size="small" />
                </Form.Item>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  Optional — overrides the generated QR (e.g. a bank/PhonePe QR).
                </Typography.Text>
              </Form.Item>
              <Form.Item name="isPrimary" label="Primary account" valuePropName="checked" tooltip="Shown on all invoices &amp; proforma">
                <Switch />
              </Form.Item>
              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          {!editing && (accounts ?? []).length === 0 && (
            <Alert type="info" showIcon message="This first account is automatically set as the primary account shown on documents." />
          )}
        </Form>
      </Modal>
    </div>
  );
}
